/**
 * The seats, as a list beside the map.
 *
 * ## Why the names are not on the picture
 *
 * They were, under every marker, and they collided the moment two seats sat
 * close together — which is most of a sofa. A label under a marker lands on the
 * row below it, and "Dobro Mesto Radi Ivica" lands on three of them. Nudging
 * them apart only moves the problem: in a tight cluster the label ends up
 * nowhere near the seat it names.
 *
 * So the marker carries a number (or the occupant's face), and the name lives
 * here, where it can be as long as it likes. Pointing at either one lights up
 * the other, and the name appears on the map for that seat alone.
 *
 * It earns its keep twice over on a phone, where a 27px marker is a poor
 * target for a thumb: these rows are full-width and tappable, so the list is
 * the way you book and the map is the picture that tells you where you will be
 * sitting.
 */

export type SeatTone = 'free' | 'taken' | 'mine' | 'blocked' | 'selected';

export interface SeatListItem {
  id: string;
  /** Matches the number drawn in the marker. Its position in the map's order. */
  index: number;
  label: string;
  tone: SeatTone;
  /** Who is in it, or why it cannot be taken. */
  note?: string;
  disabled?: boolean;
}

export function SeatList({
  items,
  activeId,
  onHover,
  onPick,
  empty,
  caption,
}: {
  items: SeatListItem[];
  activeId: string | null;
  onHover: (id: string | null) => void;
  onPick: (id: string) => void;
  empty: string;
  caption?: string;
}) {
  if (items.length === 0) return <p className="seat-list__empty">{empty}</p>;

  return (
    <div className="seat-list">
      {caption && <p className="seat-list__caption">{caption}</p>}
      <ul>
        {items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className={[
                'seat-list__row',
                `is-${item.tone}`,
                item.id === activeId ? 'is-active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              disabled={item.disabled}
              // Focus counts as pointing: a keyboard user tabbing the list
              // should see the map light up the same way a mouse does.
              onMouseEnter={() => onHover(item.id)}
              onMouseLeave={() => onHover(null)}
              onFocus={() => onHover(item.id)}
              onBlur={() => onHover(null)}
              onClick={() => onPick(item.id)}
            >
              <span className="seat-list__index">{item.index}</span>
              <span className="seat-list__name">{item.label}</span>
              {item.note && <span className="seat-list__note">{item.note}</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
